import type { Role, Session } from '@shared/types/User';
import { BusinessApiClient, getBusinessApiClient } from '@infra/api/BusinessApiClient';
import { AuthError, AuthService } from '@infra/auth/AuthService';
import { uuid } from '@shared/utils/uuid';
import { isBrowserCloudHost } from '@application/licence/CloudWebHost';
import { clearWebCompanies, rememberWebCompanies } from '@application/auth/WebCompanyChoice';
import {
  publishCloudWebEntitlements,
  publishCloudWebInstallation,
} from '@application/licence/CloudWebEntitlements';

const SESSION_KEY = AuthService.STORAGE_KEY;

function normalizeRemoteRole(raw: string): Role {
  const r = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, '_');
  if (r === 'super_admin' || r === 'superadmin') return 'super_admin';
  if (r === 'admin' || r === 'administrateur') return 'admin';
  if (r === 'rh' || r === 'gestionnaire_rh') return 'rh';
  if (r === 'comptable' || r === 'compta') return 'comptable';
  if (r === 'lecture' || r === 'lecture_seule') return 'lecture';
  if (r === 'salarie' || r === 'salarié') return 'salarie';
  return raw as Role;
}

function resolveExpireAt(apiExpiresAt: string | undefined): string {
  const minMs = Date.now() + AuthService.SESSION_DURATION_MS;
  const apiMs = apiExpiresAt ? new Date(apiExpiresAt).getTime() : NaN;
  const expireMs = Number.isFinite(apiMs) ? Math.max(apiMs, minMs) : minMs;
  return new Date(expireMs).toISOString();
}

export class RemoteAuthService {
  // Client partagé de l'application : la session relue par GET /v1/auth/me (cookie) doit être connue de TOUS les
  // appels (profil entreprise, charte, logo). Un client privé la laissait ignorée après un rechargement de page.
  constructor(private readonly api: BusinessApiClient = getBusinessApiClient()) {}

  private persistSession(session: Session): void {
    const raw = JSON.stringify(session);
    sessionStorage.setItem(SESSION_KEY, raw);
    try {
      localStorage.setItem(SESSION_KEY, raw);
    } catch {
      /* quota / mode privé */
    }
  }

  async registerFirstAdmin(
    email: string,
    password: string,
    nom: string,
    prenom: string,
    role: Role = 'admin',
  ): Promise<void> {
    await this.api.registerFirstAdmin({ email, password, nom, prenom, role });
  }

  /** Navigateur Cloud : premier administrateur d'une entreprise, avec sa clé de licence (licence valide exigée). */
  async registerFirstAdminWithLicenseKey(input: {
    licenseKey: string;
    email: string;
    password: string;
    nom: string;
    prenom: string;
  }): Promise<void> {
    await this.api.webRegisterFirstAdmin(input);
  }

  async login(email: string, password: string, licenseKey?: string, tenantId?: string): Promise<Session> {
    const result = await this.api.loginUser(email, password, licenseKey, tenantId);
    // Cabinet (navigateur) : les entreprises où ce mot de passe ouvre un compte alimentent le sélecteur d'entreprise.
    if (isBrowserCloudHost()) {
      if (result.companies && result.companies.length > 1) rememberWebCompanies(email, result.companies);
      else clearWebCompanies();
    }
    const session: Session = {
      user_id: result.user.id,
      tenant_id: result.user.tenant_id,
      role: normalizeRemoteRole(result.user.role),
      expire_le: resolveExpireAt(result.expires_at),
      // Navigateur Cloud : aucun jeton, la session est le cookie httpOnly ; ce champ n'est qu'un marqueur local.
      token: isBrowserCloudHost() ? 'cookie-session' : uuid('tok'),
    };
    this.persistSession(session);
    await publishCloudWebEntitlements(result.entitlements_jwt, result.user.tenant_id);
    await this.publishInstallationFromServer(result.user.tenant_id);
    return session;
  }

  /** Droits de la licence (GET /v1/auth/me → installation) appliqués à la session Cloud navigateur. */
  private async publishInstallationFromServer(tenantId: string): Promise<void> {
    try {
      const current = await this.api.fetchSession();
      publishCloudWebInstallation(current?.installation, tenantId);
    } catch {
      /* les droits restent fermés tant que /me n'a pas répondu */
    }
  }

  async logout(): Promise<void> {
    await this.api.logoutUser();
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  }

  /** Recharge rôle/tenant depuis business-api (anti-falsification sessionStorage). */
  async refreshSessionFromServer(): Promise<Session | null> {
    const raw = sessionStorage.getItem(SESSION_KEY) ?? localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    let session: Session;
    try {
      session = JSON.parse(raw) as Session;
    } catch {
      localStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    if (new Date(session.expire_le) <= new Date()) {
      AuthService.markSessionExpiredNotice();
      localStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    const current = await this.api.fetchSession();
    const me = current?.user ?? null;
    if (!me || !current) {
      localStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    const updated: Session = {
      ...session,
      user_id: me.id,
      tenant_id: me.tenant_id,
      role: normalizeRemoteRole(me.role),
      expire_le: resolveExpireAt(session.expire_le),
    };
    this.persistSession(updated);
    await publishCloudWebEntitlements(current.entitlements_jwt, me.tenant_id);
    publishCloudWebInstallation(current.installation, me.tenant_id);
    return updated;
  }

  async requireRoleFresh(...allowed: Role[]): Promise<Session> {
    const session = await this.refreshSessionFromServer();
    if (!session) throw new AuthError('SESSION_EXPIRED');
    if (!allowed.includes(session.role)) throw new AuthError('INSUFFICIENT_ROLE');
    return session;
  }

  static mapAuthError(err: unknown): never {
    const msg = String((err as Error).message ?? err);
    if (msg.includes('401') || msg.includes('Identifiants')) {
      throw new AuthError('INVALID_CREDENTIALS');
    }
    throw err instanceof Error ? err : new Error(msg);
  }
}
